import { useAccount, useReadContracts, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { useEffect, useState } from "react";
import type { Address } from "viem";
import { PIGGY_ORIGINALS_ABI } from "@/lib/abis";
import { ADDRESSES } from "@/lib/config";

const ZERO: Address = "0x0000000000000000000000000000000000000000";

export interface MintInfo {
    mintPrice: bigint;
    perWalletCap: bigint;
    totalMinted: bigint;
    maxSupply: bigint;
    mintActive: boolean;
    mintedByMe: bigint;
}

const DEFAULTS: MintInfo = {
    mintPrice: 60_000_000_000_000_000n, // 0.06 ETH (live value read on-chain; this is only the pre-load fallback)
    perWalletCap: 5n,
    totalMinted: 0n,
    maxSupply: 5_000n,
    mintActive: false,
    mintedByMe: 0n,
};

export function useMintInfo(): { info: MintInfo; live: boolean; refetch: () => void } {
    const { address } = useAccount();
    const contract = ADDRESSES.PIGGY_ORIGINALS;

    const targetAddr: Address = address ?? ZERO;
    const reads = useReadContracts({
        allowFailure: true,
        contracts: contract
            ? [
                  { address: contract, abi: PIGGY_ORIGINALS_ABI, functionName: "mintPrice"    } as const,
                  { address: contract, abi: PIGGY_ORIGINALS_ABI, functionName: "perWalletCap" } as const,
                  { address: contract, abi: PIGGY_ORIGINALS_ABI, functionName: "totalMinted"  } as const,
                  { address: contract, abi: PIGGY_ORIGINALS_ABI, functionName: "MAX_SUPPLY"   } as const,
                  { address: contract, abi: PIGGY_ORIGINALS_ABI, functionName: "mintActive"   } as const,
                  { address: contract, abi: PIGGY_ORIGINALS_ABI, functionName: "mintedBy", args: [targetAddr] } as const,
              ]
            : undefined,
        query: { enabled: Boolean(contract), refetchInterval: 12_000 },
    });

    if (!contract || !reads.data) return { info: DEFAULTS, live: false, refetch: () => reads.refetch() };

    const get = <T,>(i: number): T | undefined => reads.data?.[i]?.result as T | undefined;

    return {
        info: {
            mintPrice:    get<bigint>(0)  ?? DEFAULTS.mintPrice,
            perWalletCap: get<bigint>(1)  ?? DEFAULTS.perWalletCap,
            totalMinted:  get<bigint>(2)  ?? DEFAULTS.totalMinted,
            maxSupply:    get<bigint>(3)  ?? DEFAULTS.maxSupply,
            mintActive:   get<boolean>(4) ?? DEFAULTS.mintActive,
            mintedByMe:   address ? (get<bigint>(5) ?? 0n) : 0n,
        },
        live: true,
        refetch: () => reads.refetch(),
    };
}

export function useMint(quantity: bigint, mintPrice: bigint) {
    const contract = ADDRESSES.PIGGY_ORIGINALS;
    const { writeContract, data: hash, isPending, reset } = useWriteContract();
    const { isLoading: confirming, isSuccess: confirmed } = useWaitForTransactionReceipt({ hash });

    const mint = () => {
        if (!contract) return;
        writeContract({
            address: contract,
            abi: PIGGY_ORIGINALS_ABI,
            functionName: "mint",
            args: [quantity],
            value: mintPrice * quantity,
        });
    };

    useEffect(() => {
        if (confirmed) {
            const t = setTimeout(reset, 4_000);
            return () => clearTimeout(t);
        }
    }, [confirmed, reset]);

    return {
        mint,
        hash,
        status: confirmed
            ? ("confirmed" as const)
            : confirming
            ? ("confirming" as const)
            : isPending
            ? ("pending" as const)
            : ("idle" as const),
    };
}

/// Self-serve reveal: the dapp fetches each token's Merkle proof from
/// /proofs/<id>.json (committed alongside the metadata) and submits a single
/// reveal() tx. Permissionless — the holder reveals their own tokens; no
/// deployer key, no script. Writes the pre-committed rarity on-chain.
export function useReveal() {
    const contract = ADDRESSES.PIGGY_ORIGINALS;
    const { writeContract, data: hash, isPending, reset } = useWriteContract();
    const { isLoading: confirming, isSuccess: confirmed } = useWaitForTransactionReceipt({ hash });
    const [preparing, setPreparing] = useState(false);

    const reveal = async (tokenIds: bigint[]) => {
        if (!contract || tokenIds.length === 0) return;
        setPreparing(true);
        try {
            const entries = await Promise.all(
                tokenIds.map(async (id) => {
                    const res = await fetch(`/proofs/${id.toString()}.json`);
                    if (!res.ok) throw new Error(`missing proof for token ${id}`);
                    return (await res.json()) as { rarity: number; proof: `0x${string}`[] };
                }),
            );
            writeContract({
                address: contract,
                abi: PIGGY_ORIGINALS_ABI,
                functionName: "reveal",
                args: [tokenIds, entries.map((e) => e.rarity), entries.map((e) => e.proof)],
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("reveal failed to prepare:", e);
        } finally {
            setPreparing(false);
        }
    };

    useEffect(() => {
        if (confirmed) {
            const t = setTimeout(reset, 4_000);
            return () => clearTimeout(t);
        }
    }, [confirmed, reset]);

    return {
        reveal,
        hash,
        status: confirmed
            ? ("confirmed" as const)
            : confirming
            ? ("confirming" as const)
            : isPending
            ? ("pending" as const)
            : preparing
            ? ("preparing" as const)
            : ("idle" as const),
    };
}

export function useMyNFTs() {
    const { address } = useAccount();
    const contract = ADDRESSES.PIGGY_ORIGINALS;

    const balance = useReadContracts({
        allowFailure: true,
        contracts:
            contract && address
                ? [{ address: contract, abi: PIGGY_ORIGINALS_ABI, functionName: "balanceOf", args: [address] }]
                : [],
        query: { enabled: Boolean(contract && address), refetchInterval: 15_000 },
    });

    const count = (balance.data?.[0]?.result as bigint | undefined) ?? 0n;
    const indices = Array.from({ length: Number(count) }, (_, i) => BigInt(i));

    const tokens = useReadContracts({
        allowFailure: true,
        contracts:
            contract && address
                ? indices.map((i) => ({
                      address: contract,
                      abi: PIGGY_ORIGINALS_ABI,
                      functionName: "tokenOfOwnerByIndex" as const,
                      args: [address, i],
                  }))
                : [],
        query: { enabled: Boolean(contract && address && count > 0n) },
    });

    const tokenIds = (tokens.data ?? [])
        .map((r) => r.result as bigint | undefined)
        .filter((x): x is bigint => x !== undefined);

    const rarities = useReadContracts({
        allowFailure: true,
        contracts: contract
            ? tokenIds.map((id) => ({
                  address: contract,
                  abi: PIGGY_ORIGINALS_ABI,
                  functionName: "rarityOf" as const,
                  args: [id],
              }))
            : [],
        query: { enabled: Boolean(contract && tokenIds.length > 0) },
    });

    const items = tokenIds.map((id, i) => ({
        tokenId: id,
        rarity: Number((rarities.data?.[i]?.result as number | undefined) ?? 0),
    }));

    const refetch = () => {
        balance.refetch();
        tokens.refetch();
        rarities.refetch();
    };

    return { items, count: Number(count), loading: balance.isLoading, refetch };
}
